import { useQuery } from "@tanstack/react-query";
import { MetricCard } from "@/components/metric-card";
import { SourceBreakdownChart } from "@/components/source-breakdown-chart";
import { FunnelChart } from "@/components/funnel-chart";
import { DataTable } from "@/components/data-table";
import { DashboardSkeleton } from "@/components/loading-skeleton";
import { DateRangePicker } from "@/components/date-range-picker";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DateRange } from "react-day-picker";
import { useState } from "react";
import { useLocation, Link } from "wouter";
import { subDays } from "date-fns";
import {
  DollarSign,
  Users,
  FileText,
  ShoppingCart,
  RefreshCw,
  AlertCircle,
  Phone,
  Clock,
  Star,
  Wrench,
} from "lucide-react";
import type { SalesReportData, ProductMetrics } from "@re/shared";
import { getSelectedPipelines } from "./settings";

export default function Dashboard() {
  const [, navigate] = useLocation();
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: subDays(new Date(), 30),
    to: new Date(),
  });

  const queryParams = new URLSearchParams();
  if (dateRange?.from) queryParams.set("startDate", dateRange.from.toISOString());
  if (dateRange?.to) queryParams.set("endDate", dateRange.to.toISOString());
  const queryUrl = `/api/report${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;

  const { data: hubData } = useQuery({ queryKey: ["/api/business-hub"] });
  const healthScore = (hubData as any)?.businessHealth?.score ?? 520;
  const csScore = (hubData as any)?.customerService?.avgUxScore ?? 3.4;
  const salesPct = (hubData as any)?.salesConversions?.overallPercentage ?? 42;
  const healthMets: any[] = (hubData as any)?.businessHealth?.metrics ?? [
    { name: "Cash Flow", score: 72 }, { name: "Job Completion", score: 85 },
    { name: "Cust Sat", score: 58 }, { name: "Team Util", score: 45 },
    { name: "Rev Growth", score: 78 }, { name: "Profit Margin", score: 34 },
    { name: "Mktg CoS", score: 28 }, { name: "Sales Conv", score: 52 },
  ];
  const csMets: any[] = (hubData as any)?.customerService?.metrics ?? [
    { name: "Total Calls", value: 631, change: 12 },
    { name: "Inbound", value: 461, change: 8 },
    { name: "Missed", value: 178, change: -28 },
    { name: "Duration", value: "87s", change: 5 },
    { name: "Avoidable", value: 129, change: -72 },
    { name: "CSAT", value: "3.4/5", change: -6 },
  ];
  const salesFun: any[] = (hubData as any)?.salesConversions?.subMetrics ?? [
    { name: "Click-Enquiry", rate: 68, costPer: 2.40 },
    { name: "Enquiry-Quote", rate: 54, costPer: 12.50 },
    { name: "Quote-Sale", rate: 38, costPer: 45.00 },
  ];
  const { data, isLoading, error, refetch, isFetching } = useQuery<SalesReportData>({
    queryKey: [queryUrl],
  });

  if (isLoading) {
    return (
      <div className="p-6">
        <DashboardSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <AlertCircle className="h-12 w-12 text-destructive mb-4" />
            <h3 className="text-lg font-semibold mb-2">Failed to load report</h3>
            <p className="text-muted-foreground text-center mb-4">
              There was an error connecting to BigQuery. Please check your credentials.
            </p>
            <Button onClick={() => refetch()} data-testid="button-retry">
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const { overall, products: allProducts, availablePipelines, callMetrics, scheduleMetrics, activeJobs } = data;

  const selectedPipelineIds = getSelectedPipelines();
  const products = selectedPipelineIds.length > 0
    ? allProducts.filter(p => selectedPipelineIds.includes(p.pipelineId))
    : allProducts;

  const funnelData = [
    {
      label: "Opportunities",
      value: overall.totalConversions,
      percentage: 100,
      cost: overall.totalCost,
    },
    {
      label: "Active Jobs",
      value: overall.totalQuotes,
      percentage: overall.quoteRate,
      cost: 0,
    },
    {
      label: "Completed",
      value: overall.totalSales,
      percentage: overall.saleRate,
      cost: 0,
    },
  ];

  const productColumns = [
    {
      key: "pipelineName",
      header: "Product",
      render: (item: ProductMetrics) => (
        <span className="font-medium">{item.pipelineName}</span>
      ),
    },
    {
      key: "conversions",
      header: "Opportunities",
      sortable: true,
      align: "right" as const,
      render: (item: ProductMetrics) => item.conversions.toLocaleString(),
    },
    {
      key: "quotes",
      header: "Active",
      sortable: true,
      align: "right" as const,
      render: (item: ProductMetrics) => (
        <div className="flex items-center justify-end gap-2">
          <span>{item.quotes.toLocaleString()}</span>
          <Badge variant="secondary" className="text-xs">
            {item.quoteRate.toFixed(0)}%
          </Badge>
        </div>
      ),
    },
    {
      key: "sales",
      header: "Completed",
      sortable: true,
      align: "right" as const,
      render: (item: ProductMetrics) => (
        <div className="flex items-center justify-end gap-2">
          <span>{item.sales.toLocaleString()}</span>
          <Badge variant="secondary" className="text-xs">
            {item.saleRate.toFixed(0)}%
          </Badge>
        </div>
      ),
    },
    {
      key: "revenue",
      header: "Quoted Value",
      sortable: true,
      align: "right" as const,
      render: (item: ProductMetrics) => (
        <span>£{item.revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
      ),
    },
  ];

  return (
    <div className="p-6 space-y-6">
      {/* === BUSINESS PERFORMANCE TILES === */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Business Performance</h2>
          <Link href="/pro-dashboard"><span className="text-xs text-primary cursor-pointer hover:underline">View Pro Dashboard</span></Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-card border-border/60"><CardContent className="p-4">
            <div className="flex items-start justify-between mb-2">
              <div>
                <p className="text-xs font-medium uppercase text-muted-foreground">Business Health</p>
                <p className="text-xs text-muted-foreground">Target: 750/1000</p>
              </div>
              <Link href="/executive-summary"><span className="text-xs text-primary cursor-pointer">&rarr;</span></Link>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex flex-col items-center flex-shrink-0">
                <span className="text-4xl font-bold" style={{color: healthScore>=700?"#22c55e":healthScore>=400?"#f59e0b":"#ef4444"}}>{healthScore}</span>
                <span className="text-xs text-muted-foreground">/1000</span>
                <span className="text-xs font-semibold mt-0.5" style={{color: healthScore>=700?"#22c55e":healthScore>=400?"#f59e0b":"#ef4444"}}>{healthScore>=700?"Good":healthScore>=400?"Fair":"Poor"}</span>
              </div>
              <div className="flex-1 grid grid-cols-2 gap-1">
                {healthMets.map((m: any) => (
                  <div key={m.name} className="flex items-center gap-1">
                    <span className={"w-1.5 h-1.5 rounded-full flex-shrink-0 " + (m.score>=70?"bg-green-500":m.score>=40?"bg-amber-500":"bg-red-500")} />
                    <span className="truncate" style={{fontSize:"9px",color:"#9ca3af"}}>{m.name}</span>
                    <span className={"ml-auto font-bold " + (m.score>=70?"text-green-400":m.score>=40?"text-amber-400":"text-red-400")} style={{fontSize:"9px"}}>{m.score}%</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent></Card>
          <Card className="bg-card border-border/60"><CardContent className="p-4">
            <div className="flex items-start justify-between mb-2">
              <div>
                <p className="text-xs font-medium uppercase text-muted-foreground">Customer Service</p>
                <p className="text-xs text-muted-foreground">CSAT Score</p>
              </div>
              <Link href="/customer-service"><span className="text-xs text-primary cursor-pointer">&rarr;</span></Link>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex flex-col items-center flex-shrink-0">
                <span className="text-4xl font-bold" style={{color: csScore>=4?"#22c55e":csScore>=3?"#f59e0b":"#ef4444"}}>{csScore.toFixed(1)}</span>
                <span className="text-xs text-muted-foreground">/5</span>
                <span className="text-xs font-semibold mt-0.5" style={{color: csScore>=4?"#22c55e":csScore>=3?"#f59e0b":"#ef4444"}}>{csScore>=4?"Good":csScore>=3?"Fair":"Poor"}</span>
              </div>
              <div className="flex-1 space-y-1">
                {csMets.map((m: any) => (
                  <div key={m.name} className="flex items-center justify-between">
                    <span style={{fontSize:"9px",color:"#9ca3af"}}>{m.name}</span>
                    <div className="flex items-center gap-1">
                      <span style={{fontSize:"9px"}} className="font-semibold">{typeof m.value==="number"?m.value.toLocaleString():m.value}</span>
                      {m.change>0&&<span style={{fontSize:"8px"}} className="text-green-400">+{m.change}%</span>}
                      {m.change<0&&<span style={{fontSize:"8px"}} className="text-red-400">{m.change}%</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent></Card>
          <Card className="bg-card border-border/60"><CardContent className="p-4">
            <div className="flex items-start justify-between mb-2">
              <div>
                <p className="text-xs font-medium uppercase text-muted-foreground">Sales Conversions</p>
                <p className="text-xs text-muted-foreground">Click to Sale funnel</p>
              </div>
              <Link href="/marketing-intelligence"><span className="text-xs text-primary cursor-pointer">&rarr;</span></Link>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex flex-col items-center flex-shrink-0">
                <span className="text-4xl font-bold" style={{color: salesPct>=50?"#22c55e":salesPct>=30?"#f59e0b":"#ef4444"}}>{salesPct.toFixed(0)}%</span>
                <span className="text-xs text-muted-foreground">overall</span>
                <span className="text-xs font-semibold mt-0.5" style={{color: salesPct>=50?"#22c55e":salesPct>=30?"#f59e0b":"#ef4444"}}>{salesPct>=50?"Good":salesPct>=30?"Fair":"Poor"}</span>
              </div>
              <div className="flex-1 space-y-2">
                {salesFun.map((s: any, i: number) => {
                  const rate = s.rate ?? 0;
                  const cost = s.costPer ?? 0;
                  const bc = rate>=60?"bg-green-500":rate>=40?"bg-amber-500":"bg-red-500";
                  return (
                    <div key={i} className="space-y-0.5">
                      <div className="flex justify-between" style={{fontSize:"9px"}}>
                        <span style={{color:"#9ca3af"}}>{s.name}</span>
                        <span className="font-semibold">{rate}% &bull; &pound;{Number(cost).toFixed(2)}</span>
                      </div>
                      <div className="h-1.5 rounded-full overflow-hidden" style={{background:"rgba(255,255,255,0.1)"}}>
                        <div className={"h-full rounded-full " + bc} style={{width:rate+"%"}} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </CardContent></Card>
        </div>
      </div>
      {/* === END BUSINESS PERFORMANCE TILES === */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Sales Report Overview</h1>
          <p className="text-muted-foreground">
            Sales pipeline and operations performance
          </p>
        </div>
        <div className="flex items-center gap-3">
          <DateRangePicker
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
          />
          <Button
            variant="outline"
            size="icon"
            onClick={() => refetch()}
            disabled={isFetching}
            data-testid="button-refresh"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Opportunities"
          value={overall.totalConversions}
          subtitle="All pipeline entries"
          icon={<Users className="h-4 w-4" />}
          data-testid="metric-total-opportunities"
        />
        <MetricCard
          title="Jobs in Progress"
          value={activeJobs || overall.totalQuotes || 0}
          subtitle={`${overall.quoteRate?.toFixed(1) || 0}% of total`}
          icon={<Wrench className="h-4 w-4" />}
          data-testid="metric-jobs-in-progress"
        />
        <MetricCard
          title="Jobs Completed"
          value={overall.totalSales || 0}
          subtitle={`${overall.saleRate?.toFixed(1) || 0}% completion rate`}
          icon={<ShoppingCart className="h-4 w-4" />}
          data-testid="metric-jobs-completed"
        />
        <MetricCard
          title="Quoted Revenue"
          value={`£${overall.totalRevenue?.toLocaleString() || 0}`}
          subtitle="Total quoted value"
          icon={<DollarSign className="h-4 w-4" />}
          data-testid="metric-quoted-revenue"
        />
      </div>

      {(callMetrics || scheduleMetrics) && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {callMetrics && (
            <>
              <MetricCard
                title="Total Calls"
                value={callMetrics.totalCalls.toLocaleString()}
                subtitle="Phone calls tracked"
                icon={<Phone className="h-4 w-4" />}
              />
              <MetricCard
                title="Avg Call Duration"
                value={`${Math.floor(callMetrics.avgCallDuration / 60)}m ${callMetrics.avgCallDuration % 60}s`}
                subtitle="Average talk time"
                icon={<Clock className="h-4 w-4" />}
              />
              <MetricCard
                title="Call Quality Score"
                value={callMetrics.avgUxScore.toFixed(1)}
                subtitle="Out of 5.0"
                icon={<Star className="h-4 w-4" />}
              />
            </>
          )}
          {scheduleMetrics && (
            <MetricCard
              title="Active Engineers"
              value={scheduleMetrics.activeEngineers}
              subtitle={`${scheduleMetrics.totalJobs} jobs scheduled`}
              icon={<Wrench className="h-4 w-4" />}
            />
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SourceBreakdownChart
          data={overall.bySource}
          metric="conversions"
          title="Opportunities by Category"
        />
        <FunnelChart data={funnelData} title="Conversion Funnel" />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Products by Pipeline</CardTitle>
          <CardDescription>
            Click on a product to see detailed campaign breakdown
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            data={products}
            columns={productColumns}
            onRowClick={(item) => navigate(`/products/${item.pipelineId}`)}
            showDrilldown
          />
        </CardContent>
      </Card>
    </div>
  );
}
